import { type FormEvent, useEffect, useRef, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router";
import { LoadingScreen } from "../../components/LoadingScreen";
import { APP_NAME, ApiError, authApi } from "../../lib/api";
import { useAuth } from "../../lib/auth";
import { useAppPreferences } from "../../lib/preferences";
import { getValidatedSignInReturnTo } from "./provider-return-to";

const EMAIL_UNVERIFIED_DETAIL = "Please verify your email before signing in.";

function isEmailUnverifiedError(requestError: unknown) {
  return (
    requestError instanceof ApiError &&
    requestError.status === 403 &&
    requestError.detail === EMAIL_UNVERIFIED_DETAIL
  );
}

export function SignInPage() {
  const { t } = useAppPreferences();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const returnTo = searchParams.get("returnTo");
  const bookingDraftId = searchParams.get("booking_draft_id");
  const { homePath, signIn, verifyEmail, status, user } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [setupRequired, setSetupRequired] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [verificationEmail, setVerificationEmail] = useState<string | null>(null);
  const [verificationCode, setVerificationCode] = useState("");
  const [verificationError, setVerificationError] = useState<string | null>(null);
  const [verificationInfo, setVerificationInfo] = useState<string | null>(null);
  const [verifying, setVerifying] = useState(false);
  const [resending, setResending] = useState(false);
  const verificationGeneration = useRef(0);
  const codeInputRef = useRef<HTMLInputElement | null>(null);
  const verificationErrorRef = useRef<HTMLDivElement | null>(null);
  const passwordInputRef = useRef<HTMLInputElement | null>(null);

  const redirectPath = getValidatedSignInReturnTo(returnTo) ?? homePath;

  useEffect(() => {
    if (status === "authenticated" && user) {
      navigate(redirectPath, { replace: true });
    }
  }, [navigate, redirectPath, status, user]);

  useEffect(() => {
    if (verificationEmail !== null) {
      codeInputRef.current?.focus();
    }
  }, [verificationEmail]);

  useEffect(() => {
    if (verificationError !== null) {
      verificationErrorRef.current?.focus();
    }
  }, [verificationError]);

  const handleEmailChange = (value: string) => {
    if (verifying) return;
    setEmail(value);
    verificationGeneration.current += 1;
    setSubmitting(false);
    if (verificationEmail !== null) {
      setVerificationEmail(null);
      setVerificationCode("");
      setVerificationError(null);
      setVerificationInfo(null);
      setVerifying(false);
      setResending(false);
    }
  };

  const handleBackToSignIn = () => {
    if (verifying) return;
    verificationGeneration.current += 1;
    setVerificationEmail(null);
    setVerificationCode("");
    setVerificationError(null);
    setVerificationInfo(null);
    setVerifying(false);
    setResending(false);
    passwordInputRef.current?.focus();
  };

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (verifying) return;
    const generation = verificationGeneration.current + 1;
    verificationGeneration.current = generation;
    setSubmitting(true);
    setError(null);
    setSetupRequired(false);

    try {
      const session = await signIn(email, password);
      if (verificationGeneration.current !== generation) return;
      navigate(getValidatedSignInReturnTo(returnTo) ?? session.home_path, { replace: true });
    } catch (requestError) {
      if (verificationGeneration.current !== generation) return;
      if (requestError instanceof ApiError) {
        if (requestError.code === "password_setup_required") {
          setSetupRequired(true);
          setError(t("This provider-owner account still needs password setup. Check the setup email for the 7-day link, or use Forgot password to request a recovery link."));
          return;
        }
        if (isEmailUnverifiedError(requestError)) {
          const pendingEmail = email.trim();
          setError(requestError.detail);
          setVerificationEmail(pendingEmail);
          setVerificationCode("");
          setVerificationError(null);
          setVerificationInfo(null);
          setPassword("");
          return;
        }
        setError(requestError.detail);
      } else {
        setError(t("Unable to sign in. Please try again."));
      }
    } finally {
      if (verificationGeneration.current === generation) {
        setSubmitting(false);
      }
    }
  };

  const handleVerificationSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (verificationEmail === null || verifying || resending) return;
    const snapshotEmail = verificationEmail;
    const snapshotCode = verificationCode;
    const generation = verificationGeneration.current;
    setVerifying(true);
    setVerificationError(null);
    setVerificationInfo(null);

    try {
      const session = await verifyEmail({
        email: snapshotEmail,
        code: snapshotCode,
        booking_draft_id: bookingDraftId,
      });
      if (verificationGeneration.current !== generation) return;
      if (verificationEmail !== snapshotEmail) return;
      setPassword("");
      setVerificationCode("");
      navigate(getValidatedSignInReturnTo(returnTo) ?? session.home_path, { replace: true });
    } catch (requestError) {
      if (verificationGeneration.current !== generation) return;
      if (verificationEmail !== snapshotEmail) return;
      if (requestError instanceof ApiError) {
        setVerificationError(requestError.detail);
      } else {
        setVerificationError(t("Unable to verify email. Please try again."));
      }
    } finally {
      if (verificationGeneration.current === generation) {
        setVerifying(false);
      }
    }
  };

  const handleResend = async () => {
    if (verificationEmail === null || resending || verifying) return;
    const snapshotEmail = verificationEmail;
    const generation = verificationGeneration.current;
    setResending(true);
    setVerificationError(null);
    setVerificationInfo(null);

    try {
      await authApi.resendVerification(snapshotEmail);
      if (verificationGeneration.current !== generation) return;
      if (verificationEmail !== snapshotEmail) return;
      setVerificationInfo(t("A new code has been sent."));
    } catch (requestError) {
      if (verificationGeneration.current !== generation) return;
      if (verificationEmail !== snapshotEmail) return;
      if (requestError instanceof ApiError) {
        setVerificationError(requestError.detail);
      } else {
        setVerificationError(t("Unable to resend code. Please try again."));
      }
    } finally {
      if (verificationGeneration.current === generation) {
        setResending(false);
      }
    }
  };

  if (status === "loading") {
    return <LoadingScreen />;
  }

  return (
    <div className="flex min-h-screen items-center justify-center px-4 py-10">
      <div className="w-full max-w-sm space-y-6">
        <form
          onSubmit={handleSubmit}
          className="space-y-6"
        >
          <div className="text-center">
            <h1 className="text-2xl font-bold tracking-tight text-white">
              {APP_NAME}
            </h1>
            <p className="mt-2 text-sm text-slate-400">
              {t("Sign in to your workspace")}
            </p>
          </div>

          <div className="space-y-4">
            <label className="block">
              <span className="mb-1.5 block text-xs font-medium text-slate-400">
                {t("Email")}
              </span>
              <input
                autoComplete="email"
                className="ops-input w-full"
                disabled={verifying}
                onChange={(event) => handleEmailChange(event.target.value)}
                placeholder="you@example.com"
                type="email"
                value={email}
              />
            </label>
            <label className="block">
              <span className="mb-1.5 block text-xs font-medium text-slate-400">
                {t("Password")}
              </span>
              <input
                autoComplete="current-password"
                className="ops-input w-full"
                disabled={verifying}
                onChange={(event) => setPassword(event.target.value)}
                placeholder="••••••••"
                ref={passwordInputRef}
                type="password"
                value={password}
              />
            </label>
          </div>

          {error && (
            <div
              className="rounded-xl border border-rose-400/20 bg-rose-400/10 px-4 py-3 text-sm text-rose-100"
              role="alert"
            >
              {error}
            </div>
          )}
          {setupRequired ? (
            <p className="-mt-3 text-sm leading-6 text-slate-300">
              <Link className="ops-action-link font-semibold text-sky-200" to="/forgot-password">
                {t("Open Forgot password")}
              </Link>
            </p>
          ) : null}

          <button
            className="ops-button w-full"
            data-variant="primary"
            disabled={submitting || verifying}
            type="submit"
          >
            {submitting ? t("Signing in...") : t("Sign in")}
          </button>

          <div className="flex items-center justify-between text-xs text-slate-400">
            <Link className="ops-action-link text-slate-300" to="/forgot-password">
              {t("Forgot password?")}
            </Link>
            <Link className="ops-action-link text-slate-300" to="/provider-register">
              {t("Register venue")}
            </Link>
          </div>
        </form>

        {verificationEmail !== null ? (
          <form
            aria-label={t("Verify your email")}
            className="space-y-4 rounded-2xl border border-white/10 bg-white/[0.02] p-4"
            onSubmit={handleVerificationSubmit}
          >
            <div>
              <h2 className="text-sm font-semibold text-white">
                {t("Verify your email")}
              </h2>
              <p className="mt-1 text-sm leading-6 text-slate-300">
                {t("Enter the 6-digit code sent to {{email}}", { email: verificationEmail })}
              </p>
            </div>

            <label className="block">
              <span className="mb-1.5 block text-xs font-medium text-slate-400">
                {t("Verification code")}
              </span>
              <input
                autoComplete="one-time-code"
                className="ops-input min-h-[44px] w-full text-center tracking-[0.5em]"
                inputMode="numeric"
                maxLength={6}
                onChange={(event) => setVerificationCode(event.target.value.replace(/\D/g, ""))}
                placeholder="000000"
                ref={codeInputRef}
                required
                type="text"
                value={verificationCode}
              />
            </label>

            {verificationError && (
              <div
                className="rounded-xl border border-rose-400/20 bg-rose-400/10 px-4 py-3 text-sm text-rose-100"
                ref={verificationErrorRef}
                role="alert"
                tabIndex={-1}
              >
                {verificationError}
              </div>
            )}

            {verificationInfo && (
              <div
                className="rounded-xl border border-emerald-400/20 bg-emerald-400/10 px-4 py-3 text-sm text-emerald-100"
                role="status"
              >
                {verificationInfo}
              </div>
            )}

            <button
              className="ops-button min-h-[44px] w-full"
              data-variant="primary"
              disabled={verifying || resending || verificationCode.length !== 6}
              type="submit"
            >
              {verifying ? t("Verifying...") : t("Verify email")}
            </button>

            <button
              className="ops-button min-h-[44px] w-full"
              data-variant="secondary"
              disabled={resending || verifying}
              onClick={() => void handleResend()}
              type="button"
            >
              {resending ? t("Sending...") : t("Resend code")}
            </button>

            <button
              className="ops-action-link min-h-[44px] w-full text-center text-sm text-slate-300"
              disabled={verifying}
              onClick={handleBackToSignIn}
              type="button"
            >
              {t("Back to sign-in")}
            </button>
          </form>
        ) : null}
      </div>
    </div>
  );
}
